import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useAutoRefresh } from "@/hooks/useAutoRefresh";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { printQrLabels } from "@/lib/printLabels";
import PageHeader from "@/components/PageHeader";
import { labelFor, IconFor, statusCls } from "@/lib/categories";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { formatMoney } from "@/lib/money";
import { useEstablishment } from "@/context/EstablishmentContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import ClaimCodePanel from "@/components/ClaimCodePanel";
import Pagination, { paginate } from "@/components/Pagination";
import PhotoGallery from "@/components/PhotoGallery";
import { Search, PackagePlus, PackageSearch, Truck, CheckCircle2, Trash2, HeartHandshake, Gavel, Clock, Sparkles, ChevronDown, Landmark, ShieldAlert, ShieldCheck, MapPin, QrCode, Download, Printer, Check, History, ArrowLeftRight, Mail, Phone, UserRound, ArrowDownWideNarrow, PackageX, Undo2, X } from "lucide-react";
import { FindoSpinner } from "@/components/FindoSpinner";

const STATUS_FILTERS = [
  { key: "", labelKey: "c.all" },
  { key: "in_storage", labelKey: "st.in_storage" },
  { key: "matched", labelKey: "st.matched" },
  { key: "paid", labelKey: "st.paid" },
  { key: "restituted", labelKey: "st.restituted" },
];

// Weight is always displayed converted to kg (grams entered at deposit are
// converted, e.g. 500 g → 0.5 kg). Trailing zeros trimmed for readability.
function fmtWeight(kg) {
  const w = Number(kg);
  if (!(w > 0)) return null;
  const s = w.toFixed(3).replace(/\.?0+$/, "");
  return `${s} kg`;
}

// Terminal states no longer subject to an active legal retention deadline.
const J15_TERMINAL = new Set(["restituted", "shipped", "delivered", "donated", "auctioned", "destroyed", "handed_to_authorities"]);
// An item is "due soon" (J-15) when its legal deadline is 15 days away or less
// (overdue included), and it is still actively held. Uses the backend-computed
// integer `legal_days_remaining` (floored like the bell/pre-alerts) so boundary
// items (e.g. exactly 15 days out) are not dropped by fractional date math.
function isDueSoon(item) {
  if (!item || J15_TERMINAL.has(item.status)) return false;
  const d = item.legal_days_remaining;
  if (typeof d === "number") return d <= 15;
  const dl = item.legal_deadline;
  if (!dl) return false;
  const ms = new Date(dl).getTime();
  if (Number.isNaN(ms)) return false;
  return Math.floor((ms - Date.now()) / 86400000) <= 15;
}

const DISPOSITION_LABELS = (t) => ({
  auctioned: t("reg.lblAuctioned"),
  donated: t("reg.lblDonated"),
  handed_to_authorities: t("reg.lblAuthorities"),
  destroyed: t("reg.lblDestroyed"),
});

export default function Registry() {
  const { t } = useLang();
  const { scope, selectedId, establishments, load: reloadEst } = useEstablishment();
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [statusF, setStatusF] = useState("");
  const [brandF, setBrandF] = useState("");
  const [colorF, setColorF] = useState("");
  const [locationF, setLocationF] = useState("");
  const [matchOnly, setMatchOnly] = useState(false);
  const [matchSort, setMatchSort] = useState(false);
  const [unclaimedOnly, setUnclaimedOnly] = useState(false);
  const [j15Only, setJ15Only] = useState(false);
  const [selected, setSelected] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [page, setPage] = useState(1);

  function toggleSelect(id) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }
  function exitSelectMode() { setSelectMode(false); setSelectedIds(new Set()); }

  function printBatch(list) {
    const chosen = list.filter((i) => selectedIds.has(i.id));
    if (!chosen.length) return;
    if (!printQrLabels(chosen, t("reg.qrLabelsTitle"))) {
      toast.error(t("reg.popupBlocked"));
    }
  }

  const [counts, setCounts] = useState(null);
  const loadCounts = useCallback(async () => {
    try { setCounts(await api(scope(`/items/counts`))); } catch { /* non-blocking */ }
  }, [scope]);

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (statusF) params.set("status_f", statusF);
    try {
      setItems(await api(scope(`/items?${params.toString()}`)));
      loadCounts();
    } catch (e) {
      toast.error(e.message);
    }
  }, [q, statusF, scope, loadCounts]);

  useEffect(() => { load(); }, [load, selectedId]);

  // Auto-refresh: reflect status changes done by the client (payment, match)
  // without a manual reload.
  useAutoRefresh(load);

  // Deep-link: opening /b2b/registry?item=<id> (QR scan OR a click on the
  // partner cross-match bell) fetches and auto-opens that item's detail dialog.
  // Keyed on the `item` param so it also fires when already on this page.
  useEffect(() => {
    const itemId = searchParams.get("item");
    if (!itemId) return;
    (async () => {
      try {
        const it = await api(`/items/${itemId}`);
        setSelected(it);
      } catch (e) {
        toast.error(t("reg.qrNotFound"));
      } finally {
        searchParams.delete("item");
        setSearchParams(searchParams, { replace: true });
      }
    })();
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const brands = useMemo(() => {
    if (!items) return [];
    return Array.from(new Set(items.map((i) => (i.brand || "").trim()).filter(Boolean))).sort();
  }, [items]);
  const colors = useMemo(() => {
    if (!items) return [];
    return Array.from(new Set(items.map((i) => (i.color || "").trim()).filter(Boolean))).sort();
  }, [items]);
  const locations = useMemo(() => {
    if (!items) return [];
    return Array.from(new Set(items.map((i) => (i.storage_location || "").trim()).filter(Boolean))).sort();
  }, [items]);
  const shown = useMemo(() => {
    if (!items) return [];
    const list = items.filter((i) => {
      if (j15Only) return isDueSoon(i);
      if (unclaimedOnly) return i.pm_state === "unclaimed";
      if (i.pm_state === "unclaimed") return false; // hidden from every normal view
      if (brandF && (i.brand || "").trim() !== brandF) return false;
      if (colorF && (i.color || "").trim() !== colorF) return false;
      if (locationF && (i.storage_location || "").trim() !== locationF) return false;
      if (matchOnly && !i.has_public_match) return false;
      return true;
    });
    if (matchSort) {
      // Public matches first, most recent first within each group.
      return [...list].sort((a, b) => {
        const am = a.has_public_match ? 1 : 0;
        const bm = b.has_public_match ? 1 : 0;
        if (am !== bm) return bm - am;
        return String(b.created_at || "").localeCompare(String(a.created_at || ""));
      });
    }
    if (j15Only) {
      // Most urgent first (soonest / overdue deadline on top).
      return [...list].sort((a, b) => String(a.legal_deadline || "").localeCompare(String(b.legal_deadline || "")));
    }
    return list;
  }, [items, brandF, colorF, locationF, matchOnly, matchSort, unclaimedOnly, j15Only]);
  useEffect(() => { setPage(1); }, [q, statusF, brandF, colorF, locationF, matchOnly, matchSort, unclaimedOnly, j15Only, selectedId]);
  const { pageItems, pages, total, page: curPage } = paginate(shown, page);

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto">
      <PageHeader
        title={t("db.registry.t")}
        subtitle={t("db.registry.s")}
        testid="registry-header"
        action={
          <Link to="/b2b/deposit">
            <Button className="rounded-xl font-semibold" data-testid="registry-add-btn">
              <PackagePlus size={18} className="mr-1.5" /> {t("reg.add")}
            </Button>
          </Link>
        }
      />

      <DispositionSuggestions scope={scope} selectedId={selectedId} onChanged={load} />

      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="relative flex-1 min-w-56">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("reg.searchPh")}
            className="pl-10 h-11 rounded-xl bg-white" data-testid="registry-search" />
        </div>
        <div className="flex gap-2 flex-wrap">
          {STATUS_FILTERS.map((f) => {
            const cnt = counts ? (f.key ? counts[f.key] : counts.all) : null;
            const active = statusF === f.key && !j15Only;
            return (
            <button key={f.key} onClick={() => { setStatusF(f.key); setMatchOnly(false); setUnclaimedOnly(false); setJ15Only(false); }} data-testid={`filter-${f.key || "all"}`}
              className={`px-3.5 py-2 rounded-full text-sm font-semibold transition-colors inline-flex items-center gap-1.5 ${
                active ? "findo-gradient text-white" : "bg-white border border-border text-slate-600 hover:bg-slate-50"
              }`}>
              {t(f.labelKey)}
              {cnt != null && (
                <span className={`text-xs ${active ? "text-white/80" : "text-slate-400"}`} data-testid={`filter-${f.key || "all"}-count`}>
                  {cnt}
                </span>
              )}
            </button>
            );
          })}
          <button
            onClick={() => { setJ15Only(true); setStatusF(""); setMatchOnly(false); setUnclaimedOnly(false); }}
            data-testid="filter-j15"
            title={t("hc.itemsWhoseLegalDeadlineIs")}
            className={`px-3.5 py-2 rounded-full text-sm font-semibold transition-colors inline-flex items-center gap-1.5 ${
              j15Only ? "bg-amber-500 text-white" : "bg-white border border-amber-300 text-amber-700 hover:bg-amber-50"
            }`}>
            <Clock size={14} /> {t("hc.d15")}
            {items && (
              <span className={`text-xs ${j15Only ? "text-white/80" : "text-amber-500"}`} data-testid="filter-j15-count">
                {items.filter(isDueSoon).length}
              </span>
            )}
          </button>
          <button
            onClick={() => { setMatchOnly(true); setUnclaimedOnly(false); setJ15Only(false); }}
            data-testid="filter-public-match"
            title={t("hc.showOnlyItemsMatchingA")}
            className={`px-3.5 py-2 rounded-full text-sm font-semibold transition-colors inline-flex items-center gap-1.5 ${
              matchOnly ? "bg-fuchsia-600 text-white" : "bg-white border border-fuchsia-200 text-fuchsia-700 hover:bg-fuchsia-50"
            }`}>
            <Sparkles size={14} /> {t("hc.publicMatch")}
            {items && (
              <span className={`text-xs ${matchOnly ? "text-white/80" : "text-fuchsia-400"}`}>
                {items.filter((i) => i.has_public_match).length}
              </span>
            )}
          </button>
          <button
            onClick={() => setMatchSort((v) => !v)}
            data-testid="sort-public-match"
            title={t("hc.sortItemsWithAPublic")}
            className={`px-3.5 py-2 rounded-full text-sm font-semibold transition-colors inline-flex items-center gap-1.5 ${
              matchSort ? "bg-fuchsia-600 text-white" : "bg-white border border-fuchsia-200 text-fuchsia-700 hover:bg-fuchsia-50"
            }`}>
            <ArrowDownWideNarrow size={14} /> {t("hc.recentMatchesFirst")}
            {counts && (
              <span className={`text-xs ${matchSort ? "text-white/80" : "text-fuchsia-400"}`} data-testid="sort-public-match-count">
                {counts.public_match}
              </span>
            )}
          </button>
          <button
            onClick={() => { setUnclaimedOnly(true); setMatchOnly(false); setStatusF(""); setJ15Only(false); }}
            data-testid="filter-unclaimed"
            title={t("hc.unclaimedItemsMatchLeftWithout")}
            className={`px-3.5 py-2 rounded-full text-sm font-semibold transition-colors inline-flex items-center gap-1.5 ${
              unclaimedOnly ? "bg-slate-700 text-white" : "bg-white border border-slate-300 text-slate-700 hover:bg-slate-50"
            }`}>
            <PackageX size={14} /> {t("hc.unclaimedItems")}
            {items && (
              <span className={`text-xs ${unclaimedOnly ? "text-white/80" : "text-slate-400"}`}>
                {items.filter((i) => i.pm_state === "unclaimed").length}
              </span>
            )}
          </button>
        </div>
        {(brands.length > 0 || colors.length > 0 || locations.length > 0) && (
          <div className="flex gap-2 flex-wrap items-center">
            {brands.length > 0 && (
              <select value={brandF} onChange={(e) => setBrandF(e.target.value)} data-testid="registry-filter-brand"
                className="h-11 rounded-xl border border-border bg-white px-3 text-sm text-slate-600">
                <option value="">{t("reg.allBrands")}</option>
                {brands.map((b) => <option key={b} value={b}>{b}</option>)}
              </select>
            )}
            {colors.length > 0 && (
              <select value={colorF} onChange={(e) => setColorF(e.target.value)} data-testid="registry-filter-color"
                className="h-11 rounded-xl border border-border bg-white px-3 text-sm text-slate-600">
                <option value="">{t("reg.allColors")}</option>
                {colors.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            )}
            {locations.length > 0 && (
              <select value={locationF} onChange={(e) => setLocationF(e.target.value)} data-testid="registry-filter-location"
                className="h-11 rounded-xl border border-border bg-white px-3 text-sm text-slate-600">
                <option value="">{t("reg.allLocations")}</option>
                {locations.map((l) => <option key={l} value={l}>{l}</option>)}
              </select>
            )}
            {(brandF || colorF || locationF) && (
              <button onClick={() => { setBrandF(""); setColorF(""); setLocationF(""); }} data-testid="registry-filter-clear"
                className="text-sm font-semibold text-findo-indigo hover:underline px-2">{t("c.reset")}</button>
            )}
          </div>
        )}
      </div>

      {items && shown.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4" data-testid="batch-qr-bar">
          {!selectMode ? (
            <button onClick={() => setSelectMode(true)} data-testid="batch-select-toggle"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-findo-indigo hover:underline">
              <QrCode size={16} /> {t("reg.selectQr")}
            </button>
          ) : (
            <>
              <div className="flex items-center gap-3">
                <span className="text-sm font-semibold text-slate-600" data-testid="batch-selected-count">{t("reg.selectedN").replace("{n}", selectedIds.size)}</span>
                <button onClick={exitSelectMode} data-testid="batch-cancel-btn" className="text-sm font-semibold text-slate-500 hover:underline">{t("c.cancel")}</button>
              </div>
              <Button disabled={selectedIds.size === 0} onClick={() => printBatch(shown)} data-testid="batch-print-btn" className="rounded-xl font-semibold">
                <QrCode size={16} className="mr-1.5" /> {t("reg.printN").replace("{n}", selectedIds.size || "")}
              </Button>
            </>
          )}
        </div>
      )}

      {items === null ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {Array.from({ length: 8 }).map((_, i) => <div key={i} className="h-64 rounded-2xl bg-slate-100 animate-pulse" />)}
        </div>
      ) : shown.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="registry-grid">
          {pageItems.map((it) => (
            <ItemCard key={it.id} item={it}
              selectable={selectMode}
              selected={selectedIds.has(it.id)}
              onClick={() => (selectMode ? toggleSelect(it.id) : setSelected(it))} />
          ))}
        </div>
      )}
      <Pagination page={curPage} pages={pages} total={total} onPage={setPage} testid="registry-pagination" />

      <ItemDetail item={selected} establishments={establishments} onClose={() => setSelected(null)} onChanged={() => { load(); reloadEst(); setSelected(null); }} onUpdated={(it) => { setSelected(it); load(); }} />
    </div>
  );
}

function ItemCard({ item, onClick, selectable = false, selected = false }) {
  const { t, lang } = useLang();
  const Icon = IconFor(item.category);
  return (
    <button onClick={onClick} data-testid={`item-card-${item.id}`}
      className={`text-left bg-white rounded-2xl border overflow-hidden transition-all group ${
        selectable && selected ? "border-findo-indigo ring-2 ring-findo-indigo/30" : "border-border hover:border-findo-indigo hover:card-shadow"
      }`}>
      <div className="relative h-36 bg-slate-100">
        {selectable && (
          <span className={`absolute top-2 right-2 z-10 h-6 w-6 grid place-items-center rounded-md border-2 ${
            selected ? "bg-findo-indigo border-findo-indigo text-white" : "bg-white/90 border-slate-300 text-transparent"
          }`} data-testid={`item-select-${item.id}`}>
            <Check size={14} />
          </span>
        )}
        {item.image_base64 ? (
          <img src={item.image_base64} alt={item.description} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full grid place-items-center text-slate-300"><Icon size={40} /></div>
        )}
        <span className={`absolute top-2 left-2 text-[11px] font-bold px-2 py-0.5 rounded-full ${statusCls(item.status)}`}>
          {t(`st.${item.status}`)}
        </span>
        {item.has_public_match && (
          <span className={`absolute bottom-2 ${item.pm_state === "unclaimed" ? "left-2" : "right-2"} text-[10px] font-bold px-2 py-0.5 rounded-full bg-fuchsia-600 text-white flex items-center gap-1 shadow-sm`}
            title={item.pm_cancel_reason ? t("hc.activeMatchCancelReason").replace("{reason}", item.pm_cancel_reason) : t("hc.activeMatchWithAPublic")}
            data-testid={`public-match-badge-${item.id}`}>
            <Sparkles size={11} /> {t("hc.awaitingClaim")}
          </span>
        )}
        {item.pm_state === "unclaimed" && (
          <span className="absolute bottom-2 right-2 text-[10px] font-bold px-2 py-0.5 rounded-full bg-slate-700 text-white flex items-center gap-1 shadow-sm"
            title={item.pm_cancel_reason ? t("hc.unclaimedCustomerReason").replace("{reason}", item.pm_cancel_reason) : t("hc.unclaimedItemNoClaimWithin")}
            data-testid={`unclaimed-badge-${item.id}`}>
            <PackageX size={11} /> {t("hc.unclaimed")}
          </span>
        )}
        {item.status === "in_storage" && item.legal_reached && (
          <span className={`absolute ${item.has_public_match ? "bottom-9" : "bottom-2"} right-2 text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-500 text-white flex items-center gap-1`}
            title={t("reg.legalLimitCountry").replace("{date}", (item.legal_deadline || "").slice(0,10)).replace("{country}", item.legal_country)}
            data-testid={`legal-badge-${item.id}`}>
            ⚠ {t("reg.legalReached")}
          </span>
        )}
        {item.status === "in_storage" && !item.legal_reached && item.legal_prealert_active && (
          <span className={`absolute ${item.has_public_match ? "bottom-9" : "bottom-2"} right-2 text-[10px] font-bold px-2 py-0.5 rounded-full bg-orange-500 text-white flex items-center gap-1`}
            title={t("reg.legalPrealert").replace("{days}", item.legal_days_remaining)}
            data-testid={`legal-prealert-badge-${item.id}`}>
            ⏳ {t("reg.legalPrealertShort").replace("{days}", item.legal_days_remaining)}
          </span>
        )}
      </div>
      <div className="p-3">
        <div className="flex items-center gap-1.5 text-xs text-findo-indigo font-semibold">
          <Icon size={13} /> {labelFor(item.category)}
        </div>
        <p className="mt-1 text-sm font-medium text-slate-800 line-clamp-2 min-h-10">{item.description}</p>
        {item.status === "in_storage" && !item.legal_reached && item.legal_prealert_active && (
          <div className="mt-1.5 rounded-lg bg-orange-50 border border-orange-200 px-2 py-1.5" data-testid={`legal-prealert-info-${item.id}`}>
            <p className="text-[11px] font-bold text-orange-800">⏳ {t("reg.legalPrealert").replace("{days}", item.legal_days_remaining)}</p>
            <p className="text-[10px] text-orange-700 leading-tight">{t("reg.legalPrealertOptions")}</p>
          </div>
        )}
        {item.status === "in_storage" && item.legal_reached && (
          <p className="mt-1 text-[11px] text-amber-700 font-semibold" data-testid={`legal-info-${item.id}`}>
            {t("reg.legalOver").replace("{date}", (item.legal_deadline || "").slice(0, 10)).replace("{country}", item.legal_country)}
          </p>
        )}
        {(item.brand || item.color) && (
          <div className="mt-1.5 flex flex-wrap gap-1" data-testid={`item-attrs-${item.id}`}>
            {item.brand && <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{item.brand}</span>}
            {item.color && <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{item.color}</span>}
          </div>
        )}
        {item.storage_location && (
          <div className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-findo-indigo/10 text-findo-indigo" data-testid={`item-location-${item.id}`}>
            <MapPin size={11} /> {item.storage_location}
          </div>
        )}
        {item.disposition && (
          <div className="mt-1.5 rounded-lg bg-slate-50 border border-slate-200 px-2 py-1.5" data-testid={`disposition-journal-${item.id}`}>
            <p className="text-[11px] font-bold text-slate-700">📋 {t("reg.dispositionJournal")}</p>
            {(item.disposition_log && item.disposition_log.length > 0
              ? item.disposition_log
              : [{ type: item.disposition, at: item.disposed_at }]
            ).map((e, i) => (
              <p key={i} className="text-[10px] text-slate-500 leading-tight" data-testid={`disposition-entry-${item.id}-${i}`}>
                {DISPOSITION_LABELS(t)[e.type] || e.type} · {e.at ? new Date(e.at).toLocaleString(document.documentElement.lang || "fr-FR") : "—"}
                {e.note ? <span className="text-slate-600 italic"> — {e.note}</span> : null}
              </p>
            ))}
          </div>
        )}
        <div className="mt-2 flex items-center justify-between">
          <span className="text-sm font-bold text-slate-900">{formatMoney(item.recovery_fee_eur, lang)}</span>
          {item.shipping_available && <Truck size={14} className="text-slate-400" />}
        </div>
      </div>
    </button>
  );
}

function EmptyState() {
  const { t } = useLang();
  return (
    <div className="text-center py-24 border-2 border-dashed border-border rounded-2xl" data-testid="registry-empty">
      <div className="mx-auto h-16 w-16 grid place-items-center rounded-2xl bg-findo-indigo/10 text-findo-indigo">
        <PackageSearch size={32} />
      </div>
      <h3 className="mt-4 font-display font-bold text-lg text-slate-900">{t("reg.emptyTitle")}</h3>
      <p className="mt-1 text-slate-500 text-sm">{t("reg.emptyDesc")}</p>
      <Link to="/b2b/deposit"><Button className="mt-5 rounded-xl font-semibold"><PackagePlus size={18} className="mr-1.5" /> {t("reg.emptyCta")}</Button></Link>
    </div>
  );
}

const LEGAL_DISMISS_KEY = (pid) => `findo_legal_dismissed_${pid || "default"}`;
function readDismissed(pid) {
  try { return new Set(JSON.parse(localStorage.getItem(LEGAL_DISMISS_KEY(pid)) || "[]")); }
  catch { return new Set(); }
}

function DispositionSuggestions({ scope, selectedId, onChanged }) {
  const { t } = useLang();
  const { partner } = useAuth();
  const pid = partner?.id;
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(true);
  const [dismissedIds, setDismissedIds] = useState(() => readDismissed(pid));
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    try { setData(await api(scope(`/items/legal-alerts`))); }
    catch { /* ignore */ }
  }, [scope]);

  useEffect(() => { load(); }, [load, selectedId]);

  // Keep the stored dismissed set bounded: drop ids that are no longer overdue.
  useEffect(() => {
    if (!data?.items) return;
    const current = new Set(data.items.map((it) => it.id));
    const pruned = new Set([...dismissedIds].filter((id) => current.has(id)));
    if (pruned.size !== dismissedIds.size) {
      setDismissedIds(pruned);
      try { localStorage.setItem(LEGAL_DISMISS_KEY(pid), JSON.stringify([...pruned])); } catch { /* ignore */ }
    }
  }, [data, dismissedIds, pid]);

  const visibleItems = (data?.items || []).filter((it) => !dismissedIds.has(it.id));

  function dismissBanner() {
    const next = new Set([...dismissedIds, ...(data?.items || []).map((it) => it.id)]);
    setDismissedIds(next);
    try { localStorage.setItem(LEGAL_DISMISS_KEY(pid), JSON.stringify([...next])); } catch { /* ignore */ }
  }

  async function dispose(id, disposition) {
    setBusyId(id);
    try {
      await api(`/items/${id}/dispose`, { method: "POST", body: { disposition } });
      const labels = { destroyed: t("reg.lblDestroyed"), donated: t("reg.lblDonated"), auctioned: t("reg.lblAuctioned"), handed_to_authorities: t("reg.lblAuthorities") };
      toast.success(t("reg.markedAs").replace("{label}", labels[disposition]));
      await load();
      onChanged?.();
    } catch (e) { toast.error(e.message); } finally { setBusyId(null); }
  }

  if (!data || visibleItems.length === 0) return null;

  return (
    <div className="relative mb-6 rounded-2xl border border-amber-300 bg-amber-50 overflow-hidden" data-testid="disposition-suggestions">
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center gap-3 p-4 pr-16 text-left" data-testid="dispo-suggestions-toggle">
        <span className="h-10 w-10 grid place-items-center rounded-xl bg-amber-100 text-amber-700 shrink-0"><ShieldAlert size={20} /></span>
        <div className="flex-1 min-w-0">
          <p className="font-display font-bold text-amber-900">
            {t("reg.legalCount").replace("{n}", visibleItems.length)}
          </p>
          <p className="text-sm text-amber-800">{t("reg.legalHint")}</p>
        </div>
        <ChevronDown size={20} className={`text-amber-700 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      <button onClick={dismissBanner} aria-label={t("hc.close")} title={t("hc.close")}
        className="absolute top-3 right-3 z-10 h-8 w-8 grid place-items-center rounded-full bg-amber-600 text-white ring-1 ring-amber-700 shadow-sm hover:bg-amber-700 transition-colors"
        data-testid="dispo-suggestions-dismiss">
        <X size={18} strokeWidth={2.5} />
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-2" data-testid="dispo-suggestions-list">
          {visibleItems.map((it) => (
            <div key={it.id} className="flex items-center gap-3 bg-white rounded-xl border border-amber-200 p-3" data-testid={`dispo-suggestion-${it.id}`}>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-slate-800 truncate">{it.description || labelFor(it.category)}</p>
                <p className="text-xs text-slate-400 flex items-center gap-1.5 flex-wrap">
                  <span>{labelFor(it.category)}</span> · <Clock size={11} /> {t("reg.limitShort")} {(it.legal_deadline || "").slice(0, 10)}
                  <span className="text-amber-700 font-semibold">· {t("reg.overBy").replace("{n}", it.days_over)}</span>
                  <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold text-slate-600">{it.legal_country}</span>
                </p>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <button title={t("reg.tAuction")} disabled={busyId === it.id} onClick={() => dispose(it.id, "auctioned")}
                  data-testid={`suggest-auction-${it.id}`} className="p-2 rounded-lg text-cyan-700 hover:bg-cyan-50 disabled:opacity-50"><Gavel size={17} /></button>
                <button title={t("reg.tDonate")} disabled={busyId === it.id} onClick={() => dispose(it.id, "donated")}
                  data-testid={`suggest-donate-${it.id}`} className="p-2 rounded-lg text-violet-700 hover:bg-violet-50 disabled:opacity-50"><HeartHandshake size={17} /></button>
                <button title={t("reg.tAuthorities")} disabled={busyId === it.id} onClick={() => dispose(it.id, "handed_to_authorities")}
                  data-testid={`suggest-authorities-${it.id}`} className="p-2 rounded-lg text-slate-700 hover:bg-slate-100 disabled:opacity-50"><Landmark size={17} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ItemDetail({ item, establishments = [], onClose, onChanged, onUpdated }) {
  const { t } = useLang();
  const { partner } = useAuth();
  const [busy, setBusy] = useState(false);
  const [shipAvail, setShipAvail] = useState(false);
  const [shipQuote, setShipQuote] = useState(null); // {loading, available, price, margin_pct}
  const [fee, setFee] = useState("");
  const [transferTo, setTransferTo] = useState("");
  const [loc, setLoc] = useState("");
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [dispositionNote, setDispositionNote] = useState("");
  useEffect(() => { setConfirmCancel(false); }, [item]);

  useEffect(() => {
    if (item) {
      setShipAvail(!!item.shipping_available);
      setFee(String(item.recovery_fee_eur ?? ""));
      setTransferTo(item.establishment_id || "");
      setLoc(item.storage_location || "");
    }
  }, [item]);

  // Dynamic Sendcloud shipping-rate preview (weight-based, to the partner country).
  // Replaces the old manual price field — the price is never entered by hand.
  useEffect(() => {
    if (!item || !shipAvail || !(Number(item.weight_kg) > 0) || !partner?.id) { setShipQuote(null); return; }
    let cancelled = false;
    setShipQuote({ loading: true });
    (async () => {
      try {
        const q = new URLSearchParams({ partner_id: partner.id, to_country: partner.country || "FR", weight_kg: String(item.weight_kg) });
        const r = await api(`/shipping/sendcloud/methods?${q.toString()}`);
        if (cancelled) return;
        const cheapest = (r.methods || []).reduce((m, x) => (m == null || x.client_price < m ? x.client_price : m), null);
        setShipQuote({ loading: false, available: !!r.available, price: cheapest, margin_pct: r.margin_pct });
      } catch { if (!cancelled) setShipQuote({ loading: false, available: false }); }
    })();
    return () => { cancelled = true; };
  }, [item, shipAvail, partner?.id, partner?.country]);

  if (!item) return null;
  const Icon = IconFor(item.category);
  const qrSrc = `${API}/items/${item.id}/qr.png`;

  function printLabel() {
    if (!printQrLabels([{ ...item, storage_location: loc }], t("reg.qrLabelTitle"))) {
      toast.error(t("reg.popupBlockedOne"));
    }
  }

  async function restitute() {
    setBusy(true);
    try {
      await api(`/items/${item.id}/restitute`, { method: "POST" });
      toast.success(t("reg.toastRestituted"));
      onChanged();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  }
  async function saveShipping() {
    setBusy(true);
    try {
      await api(`/items/${item.id}/shipping-option`, { method: "POST", body: { available: shipAvail } });
      toast.success(t("reg.toastShipping"));
      onChanged();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  }
  async function saveFee() {
    setBusy(true);
    try {
      await api(`/items/${item.id}/fee`, { method: "PATCH", body: { recovery_fee_eur: fee === "" ? null : Number(fee) } });
      toast.success(t("reg.toastFee"));
      onChanged();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  }
  async function saveLocation() {
    setBusy(true);
    try {
      await api(`/items/${item.id}/storage-location`, { method: "PATCH", body: { storage_location: loc.trim() } });
      toast.success(t("reg.toastLocation"));
      const fresh = await api(`/items/${item.id}`);
      if (onUpdated) onUpdated(fresh); else onChanged();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  }
  async function doTransfer() {
    if (!transferTo || transferTo === item.establishment_id) return;
    setBusy(true);
    try {
      const res = await api(`/items/${item.id}/establishment`, { method: "PATCH", body: { establishment_id: transferTo } });
      toast.success(t("reg.toastTransferred").replace("{name}", res.establishment_name || t("reg.establishmentFallback")));
      onChanged();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  }
  async function dispose(disposition) {
    if (disposition === "handed_to_authorities" && !dispositionNote.trim()) {
      toast.error(t("reg.dispositionNoteRequired"));
      return;
    }
    setBusy(true);
    try {
      await api(`/items/${item.id}/dispose`, { method: "POST", body: { disposition, note: dispositionNote.trim() || undefined } });
      const labels = { destroyed: t("reg.lblDestroyed"), donated: t("reg.lblDonated"), auctioned: t("reg.lblAuctionedShort") };
      toast.success(t("reg.markedAs").replace("{label}", labels[disposition]));
      setDispositionNote("");
      onChanged();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  }
  async function cancelDeposit() {
    setBusy(true);
    try {
      const r = await api(`/items/${item.id}/cancel-deposit`, { method: "POST" });
      toast.success(r.action === "removed"
        ? t("hc.depositCancelledItemRemovedFrom")
        : t("hc.depositCancelledItemPutBack"));
      onChanged();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  }
  async function reclaimReset() {
    setBusy(true);
    try {
      await api(`/items/${item.id}/reclaim-reset`, { method: "POST" });
      toast.success(t("hc.itemPutBackInStorage"));
      onChanged();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  }

  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-lg max-h-[90vh] overflow-y-auto"
        data-testid="item-detail-dialog"
        onPointerDownOutside={(e) => { if (document.querySelector('[data-photogallery-lightbox]')) e.preventDefault(); }}
        onInteractOutside={(e) => { if (document.querySelector('[data-photogallery-lightbox]')) e.preventDefault(); }}
        onFocusOutside={(e) => { if (document.querySelector('[data-photogallery-lightbox]')) e.preventDefault(); }}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display">
            <Icon size={20} className="text-findo-indigo" /> {labelFor(item.category)}
          </DialogTitle>
          <DialogDescription>{t("reg.detailDesc")}</DialogDescription>
        </DialogHeader>
        {(item.image_base64 || item.image_base64_2) && (
          <PhotoGallery photos={[item.image_base64, item.image_base64_2]} alt={item.description}
            imgClassName="w-full h-52 object-cover rounded-xl" testid="registry-item-gallery" />
        )}
        <p className="text-slate-700">{item.description}</p>
        {item.pm_state === "unclaimed" && (
          <div className="rounded-xl border border-slate-300 bg-slate-50 p-3.5" data-testid="unclaimed-detail-section">
            <p className="text-[11px] font-bold uppercase tracking-wide text-slate-700 flex items-center gap-1.5"><PackageX size={13} /> {t("hc.unclaimedItem")}</p>
            <p className="text-[11px] text-slate-500 mt-0.5">{t("hc.matchNoClaimInTime")}{item.pm_cancel_reason ? ` ${t("hc.customerReasonFmt").replace("{reason}", item.pm_cancel_reason)}` : ""}</p>
            <Button size="sm" onClick={reclaimReset} disabled={busy} className="mt-2.5 h-8 text-xs rounded-lg" data-testid="reclaim-reset-btn">
              <Undo2 size={14} className="mr-1" /> {t("hc.putBackInStorage")}
            </Button>
          </div>
        )}
        {item.has_public_match && item.public_match_owner && item.public_match_owner.length > 0 && (
          <div className="rounded-xl border border-fuchsia-200 bg-fuchsia-50/60 p-3.5" data-testid="public-match-owner-section">
            <p className="text-[11px] font-bold uppercase tracking-wide text-fuchsia-700 flex items-center gap-1.5">
              <Sparkles size={13} /> {t("hc.potentialOwner")}
            </p>
            <p className="text-[11px] text-slate-500 mt-0.5">{t("hc.customerWhosePublicListingMatches")}</p>
            <div className="mt-2.5 space-y-2.5">
              {item.public_match_owner.map((o) => (
                <div key={o.ad_id} className="text-sm border-t border-fuchsia-100 first:border-0 first:pt-0 pt-2.5" data-testid={`public-match-owner-${o.ad_id}`}>
                  <p className="font-semibold text-slate-800 flex items-center gap-1.5"><UserRound size={14} className="text-fuchsia-600" /> {o.name || "—"}</p>
                  {o.email && (
                    <a href={`mailto:${o.email}`} className="mt-1 flex items-center gap-1.5 text-slate-600 hover:text-findo-indigo break-all" data-testid={`public-match-owner-email-${o.ad_id}`}>
                      <Mail size={13} className="shrink-0 text-slate-400" /> {o.email}
                    </a>
                  )}
                  {o.phone && (
                    <a href={`tel:${o.phone}`} className="mt-1 flex items-center gap-1.5 text-slate-600 hover:text-findo-indigo" data-testid={`public-match-owner-phone-${o.ad_id}`}>
                      <Phone size={13} className="shrink-0 text-slate-400" /> {o.phone}
                    </a>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
        {item.client_note && (
          <div className="rounded-xl bg-amber-50 border border-amber-200 px-3 py-2" data-testid="registry-client-note">
            <p className="text-[11px] font-bold uppercase tracking-wide text-amber-700">{t("dep.depositorNote")}</p>
            <p className="text-sm text-amber-900 mt-0.5 whitespace-pre-line">{item.client_note}</p>
          </div>
        )}
        {!(item.declaration_id && (item.status === "matched" || item.status === "paid")) && Array.isArray(item.verification_questions) && item.verification_questions.filter((q) => (q || "").trim()).length > 0 && (
          <div className="rounded-xl border border-indigo-200 bg-indigo-50/70 px-3.5 py-3" data-testid="item-verification-questions">
            <p className="text-[11px] font-bold uppercase tracking-wide text-indigo-700 flex items-center gap-1.5">
              <ShieldCheck size={13} /> {t("vq.title")}
            </p>
            <p className="text-[11px] text-slate-500 mt-0.5">
              {t("vq.registryRef")}
            </p>
            <ol className="mt-2 space-y-1.5 list-decimal list-inside">
              {item.verification_questions.filter((q) => (q || "").trim()).map((q, i) => (
                <li key={i} className="text-sm text-slate-800" data-testid={`verification-question-${i}`}>{q}</li>
              ))}
            </ol>
          </div>
        )}
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <Badge className={statusCls(item.status)}>{t(`st.${item.status}`)}</Badge>
          {!["restituted", "destroyed", "donated", "auctioned", "handed_to_authorities", "shipped", "delivered"].includes(item.status) && (
            !confirmCancel ? (
            <Button variant="ghost" size="sm" onClick={() => setConfirmCancel(true)}
              className="h-8 text-xs rounded-lg text-red-600 hover:bg-red-50 hover:text-red-700"
              data-testid="cancel-deposit-btn">
              <Trash2 size={14} className="mr-1" /> {t("hc.cancelTheDeposit")}
            </Button>
          ) : (
            <div className="flex items-center gap-1.5" data-testid="cancel-deposit-confirm-bar">
              <span className="text-xs font-medium text-red-700">{t("hc.correctThisEntry")}</span>
              <Button size="sm" onClick={cancelDeposit} disabled={busy}
                className="h-7 text-xs rounded-lg bg-red-600 hover:bg-red-700 text-white"
                data-testid="cancel-deposit-confirm">{busy ? <FindoSpinner size={16} /> : t("hc.yesCancel")}</Button>
              <Button size="sm" variant="ghost" onClick={() => setConfirmCancel(false)}
                className="h-7 text-xs rounded-lg" data-testid="cancel-deposit-abort">{t("hc.no")}</Button>
            </div>
          ))}
        </div>
        {confirmCancel && (
          <p className="text-[11px] text-slate-400 -mt-1" data-testid="cancel-deposit-hint">
            {item.status === "in_storage" && !item.declaration_id
              ? t("hc.theItemWillBeRemoved")
              : t("hc.theItemWillBeSet")}
          </p>
        )}

        {item.disposition && (
          <div className="rounded-xl bg-slate-50 border border-slate-200 px-3 py-2.5" data-testid="detail-disposition-journal">
            <p className="text-xs font-bold text-slate-700 flex items-center gap-1.5">📋 {t("reg.dispositionJournal")}</p>
            <div className="mt-1 space-y-0.5">
              {(item.disposition_log && item.disposition_log.length > 0
                ? item.disposition_log
                : [{ type: item.disposition, at: item.disposed_at }]
              ).map((e, i) => (
                <p key={i} className="text-xs text-slate-600" data-testid={`detail-disposition-entry-${i}`}>
                  <span className="font-semibold text-slate-800">{DISPOSITION_LABELS(t)[e.type] || e.type}</span>
                  {" · "}{e.at ? new Date(e.at).toLocaleString(document.documentElement.lang || "fr-FR") : "—"}
                  {e.note ? <span className="block text-slate-500 italic mt-0.5">📝 {e.note}</span> : null}
                </p>
              ))}
            </div>
          </div>
        )}

        <div className="space-y-3 border-t border-border pt-4">
          <div>
            <label className="text-sm font-semibold text-slate-700">{t("reg.recoveryFee")}</label>
            <div className="flex gap-2 mt-1.5">
              <Input type="number" step="0.01" value={fee} onChange={(e) => setFee(e.target.value)} className="h-10 rounded-xl" data-testid="fee-input" />
              <Button variant="outline" onClick={saveFee} disabled={busy} className="rounded-xl">{t("c.save")}</Button>
            </div>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold text-slate-700">{t("reg.shippingPossible")}</span>
            <div className="flex items-center gap-2">
              <Switch checked={shipAvail} onCheckedChange={setShipAvail} data-testid="shipping-switch" />
              <Button variant="outline" onClick={saveShipping} disabled={busy} className="rounded-xl h-8 text-xs" data-testid="shipping-save">{t("c.save")}</Button>
            </div>
          </div>
          {shipAvail && (
            <div className="rounded-xl border border-border bg-findo-cream/60 p-3 space-y-1" data-testid="shipping-quote">
              {Number(item.weight_kg) > 0 && (
                <p className="text-sm text-slate-700" data-testid="item-weight-display">{t("hc.itemWeight")} <b className="text-findo-navy" data-testid="item-weight-value">{fmtWeight(item.weight_kg)}</b></p>
              )}
              {!(Number(item.weight_kg) > 0) ? (
                <p className="text-sm text-slate-500">{t("hc.weightNotProvidedTheShipping")}</p>
              ) : shipQuote?.loading ? (
                <p className="text-sm text-slate-500">{t("hc.calculatingTheShippingRate")}</p>
              ) : shipQuote?.available && shipQuote?.price != null ? (
                <p className="text-sm text-slate-700">{t("hc.shipRateForWeight").replace("{m}", shipQuote.margin_pct ?? 15).replace("{w}", fmtWeight(item.weight_kg))} <b className="text-findo-navy" data-testid="shipping-quote-price">{Number(shipQuote.price).toFixed(2)} €</b></p>
              ) : (
                <p className="text-sm text-slate-500">{t("hc.shipRateAutoWeight").replace("{w}", fmtWeight(item.weight_kg))}</p>
              )}
            </div>
          )}
          <div data-testid="item-storage-field" className="border-t border-border pt-3">
            <label className="text-sm font-semibold text-slate-700 flex items-center gap-1.5"><MapPin size={15} className="text-findo-indigo" /> {t("reg.physicalLocation")}</label>
            <div className="flex gap-2 mt-1.5">
              <Input value={loc} onChange={(e) => setLoc(e.target.value)} maxLength={60}
                placeholder={t("reg.storagePh")} className="h-10 rounded-xl" data-testid="storage-input" />
              <Button variant="outline" onClick={saveLocation} disabled={busy} className="rounded-xl" data-testid="storage-save-btn">{t("c.save")}</Button>
            </div>
            <div className="mt-3 flex items-center gap-3 rounded-xl border border-border bg-slate-50 p-3" data-testid="item-qr-block">
              <img src={qrSrc} alt={t("reg.qrCodeItem")} className="h-24 w-24 rounded-lg bg-white p-1 border border-border" data-testid={`item-qr-${item.id}`} />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-bold text-slate-700 flex items-center gap-1.5"><QrCode size={14} /> {t("reg.qrCodeItem")}</p>
                <p className="text-[11px] text-slate-400 mt-0.5 mb-2">{t("reg.qrScanHint")}</p>
                <div className="flex gap-2">
                  <a href={qrSrc} download={`findloop-qr-${item.id}.png`} data-testid="qr-download-btn"
                    className="inline-flex items-center gap-1 text-xs font-semibold text-findo-indigo hover:underline"><Download size={13} /> {t("c.download")}</a>
                  <button type="button" onClick={printLabel} data-testid="qr-print-btn"
                    className="inline-flex items-center gap-1 text-xs font-semibold text-slate-600 hover:underline"><Printer size={13} /> {t("reg.printLabel")}</button>
                </div>
              </div>
            </div>
          </div>
          {Array.isArray(item.location_history) && item.location_history.length > 0 && (
            <div data-testid="item-history-field" className="border-t border-border pt-3">
              <p className="text-sm font-semibold text-slate-700 flex items-center gap-1.5"><History size={15} className="text-findo-indigo" /> {t("reg.moveHistory")}</p>
              <ul className="mt-2 space-y-1.5 max-h-40 overflow-y-auto">
                {[...item.location_history].reverse().map((h, i) => (
                  <li key={i} className="text-xs text-slate-600 flex items-start gap-2" data-testid={`history-entry-${i}`}>
                    <span className="mt-0.5 h-4 w-4 grid place-items-center rounded-full bg-findo-indigo/10 text-findo-indigo shrink-0">
                      {h.type === "establishment" ? <ArrowLeftRight size={10} /> : <MapPin size={10} />}
                    </span>
                    <span>
                      {h.type === "establishment" ? t("est.label") : t("reg.location")} :{" "}
                      <b>{h.from || "—"}</b> → <b>{h.to || "—"}</b>
                      <span className="text-slate-400"> · {(h.at || "").slice(0, 10)}</span>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {establishments.length > 1 && (
            <div data-testid="item-transfer-field">
              <label className="text-sm font-semibold text-slate-700">{t("est.label")}</label>
              <div className="flex gap-2 mt-1.5">
                <select
                  data-testid="item-transfer-select"
                  value={transferTo}
                  onChange={(e) => setTransferTo(e.target.value)}
                  className="flex-1 h-10 rounded-xl border border-border bg-white px-3 text-sm font-semibold text-slate-800 focus:outline-none focus:ring-2 focus:ring-findo-indigo/30"
                >
                  {establishments.map((e) => (
                    <option key={e.id} value={e.id}>{e.name}{e.is_primary ? ` · ${t("est.primary")}` : ""}</option>
                  ))}
                </select>
                <Button
                  variant="outline"
                  onClick={doTransfer}
                  disabled={busy || !transferTo || transferTo === item.establishment_id}
                  data-testid="item-transfer-btn"
                  className="rounded-xl"
                >
                  {t("c.transfer")}
                </Button>
              </div>
            </div>
          )}
          {item.status === "in_storage" && (
            <div data-testid="item-disposition-field" className="pt-2 border-t border-border">
              {!item.legal_reached && item.legal_prealert_active && (
                <div className="mb-2 rounded-lg bg-orange-50 border border-orange-200 px-3 py-2" data-testid="item-legal-prealert-banner">
                  <p className="text-xs font-bold text-orange-900">⏳ {t("reg.legalPrealert").replace("{days}", item.legal_days_remaining)}</p>
                  <p className="text-[11px] text-orange-800">{t("reg.legalPrealertOptions")}</p>
                </div>
              )}
              {item.legal_reached && (
                <div className="mb-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2" data-testid="item-legal-banner">
                  <p className="text-xs font-bold text-amber-900">⚠️ {t("reg.legalReachedFull")}</p>
                  <p className="text-[11px] text-amber-800">
                    {t("reg.legalLimitCountry").replace("{date}", (item.legal_deadline || "").slice(0, 10)).replace("{country}", item.legal_country)}
                  </p>
                </div>
              )}
              <label className="text-sm font-semibold text-slate-700">{t("reg.unclaimedDisposition")}</label>
              <p className="text-xs text-slate-400 mt-0.5 mb-2">{t("reg.dispositionHint")}</p>
              <input
                type="text"
                value={dispositionNote}
                onChange={(e) => setDispositionNote(e.target.value)}
                maxLength={300}
                placeholder={t("reg.dispositionNotePh")}
                className="w-full mb-2 rounded-xl border border-border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-findo-indigo/30"
                data-testid="disposition-note-input"
              />
              <p className="text-[11px] text-amber-700 mb-2 -mt-1" data-testid="disposition-note-authorities-hint">
                {t("reg.dispositionNoteAuthHint")}
              </p>
              <div className="grid grid-cols-2 gap-2">
                <Button variant="outline" onClick={() => dispose("auctioned")} disabled={busy}
                  data-testid="dispose-auctioned-btn" className="rounded-xl flex-col h-auto py-2.5 text-cyan-700 hover:bg-cyan-50 border-cyan-200">
                  <Gavel size={17} /><span className="text-[11px] font-semibold mt-1">{t("reg.dispAuction")}</span>
                </Button>
                <Button variant="outline" onClick={() => dispose("donated")} disabled={busy}
                  data-testid="dispose-donated-btn" className="rounded-xl flex-col h-auto py-2.5 text-violet-700 hover:bg-violet-50 border-violet-200">
                  <HeartHandshake size={17} /><span className="text-[11px] font-semibold mt-1">{t("reg.dispDonate")}</span>
                </Button>
                <Button variant="outline" onClick={() => dispose("handed_to_authorities")} disabled={busy || !dispositionNote.trim()}
                  title={!dispositionNote.trim() ? t("reg.dispositionNoteRequired") : undefined}
                  data-testid="dispose-authorities-btn" className="rounded-xl flex-col h-auto py-2.5 text-slate-700 hover:bg-slate-100 border-slate-300 disabled:opacity-50">
                  <Landmark size={17} /><span className="text-[11px] font-semibold mt-1">{t("reg.dispAuthorities")}</span>
                </Button>
                <Button variant="outline" onClick={() => dispose("destroyed")} disabled={busy}
                  data-testid="dispose-destroyed-btn" className="rounded-xl flex-col h-auto py-2.5 text-rose-700 hover:bg-rose-50 border-rose-200">
                  <Trash2 size={17} /><span className="text-[11px] font-semibold mt-1">{t("reg.dispDestroyed")}</span>
                </Button>
              </div>
            </div>
          )}
        </div>

        {item.declaration_id && (item.status === "matched" || item.status === "paid") && (
          <ClaimCodePanel declId={item.declaration_id} requirePhoto verificationQuestions={item.verification_questions} onChange={() => onUpdated?.(item)} />
        )}

        {item.status !== "restituted" && (
          <Button onClick={restitute} disabled={busy} className="w-full rounded-xl font-semibold" data-testid="restitute-btn">
            <CheckCircle2 size={18} className="mr-1.5" /> {t("reg.markRestituted")}
          </Button>
        )}
      </DialogContent>
    </Dialog>
  );
}
